import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  Modal,
  EditableText,
  Flex,
  Search,
  List,
  Text
} from "@theorgchartdev/storybook";
import cx from "classnames";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import { useTableManagerContext } from "@/providers/AppendTableProvider";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import * as React from "react";
import { useWSMgrContext } from "@/providers/WsMgrProvider";
import { appstr } from "@coreapp/utils/appstr";
import { useAppendTableManagement } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/DataSources/AppendTable/useAppendTableManagement";
import { handleListItemClick } from "@/Utils/ComponentLib";
import { useCallback, useMemo } from "react";
import { DATA_SOURCES_TEST_PREFIX } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/Utils";

export const ManageAppendTablesModal = () => {
  const wsMgrContext = useWSMgrContext();
  const wsMgr = wsMgrContext?.wsMgr?.state;
  const { showTableManager, setShowTableManager, setShowCreateAppendTableModal } =
    useTableManagerContext();

  const { selectedTableID, setSelectedTableID, appendTables, openTable, handleManageTableAction } =
    useAppendTableManagement(wsMgr);

  const [searchKey, setSearchKey] = React.useState("");
  const searchPlaceholder = appstr("Search for table name");
  const dataTestIdPrefix = "append-table-mgr";

  const createTable = () => {
    setShowCreateAppendTableModal(true);
  };

  const onClose = () => {
    setShowTableManager(false);
  };

  const handleSearchInput = value => {
    setSearchKey(value.trim());
  };

  const onSingleClick = option => setSelectedTableID(option.value);
  const onDoubleClick = option => openTable(option.value);

  const BuildTableOptions = useCallback(
    (tables, searchTerm) => {
      const filteredTables = searchTerm
        ? tables.filter(table => table.name.toLowerCase().includes(searchTerm.toLowerCase()))
        : tables;

      return filteredTables.map(table => {
        const tableData = { ID: table.ID, name: table.name };
        return {
          value: table.ID,
          clickHandler: handleListItemClick(onSingleClick, onDoubleClick),
          content: (
            <ActionListItem
              labelProps={{ size: "small" }}
              contentBefore={
                <EditableText
                  className="oc-fill-width"
                  dataTestId={`${dataTestIdPrefix}-table-${table.ID}`}
                  size={"small"}
                  bold={true}
                  text={table.name}
                  onChange={newName =>
                    handleManageTableAction("rename", { ...tableData, name: newName }, true)
                  }
                />
              }
              actions={[
                {
                  iconName: "pen-to-square",
                  onClick: () => handleManageTableAction("open", tableData),
                  dataTestId: `${table.name}-edit-table`
                },
                {
                  iconName: "trash-can",
                  iconProps: { color: "error" },
                  dataTestId: `${table.name}-delete-table`,
                  onClick: () => handleManageTableAction("delete", tableData)
                }
              ]}
            />
          )
        };
      });
    },
    [handleManageTableAction, onSingleClick, onDoubleClick]
  );

  const options = useMemo(
    () => BuildTableOptions(appendTables, searchKey),
    [appendTables, searchKey, BuildTableOptions]
  );

  return (
    <CentralizedModalContainer show={showTableManager}>
      <Modal
        id={"manage-append-table-modal"}
        dataTestId={`${DATA_SOURCES_TEST_PREFIX}-append-table-mgr-modal`}
        show={showTableManager}
        onClose={onClose}
        minWidth={"large"}
        maxWidth={"large"}
        withShadow={true}
        centerScreen={true}
        fullScreen={true}
      >
        <DialogTitle
          divider={true}
          onClose={onClose}
          data-testid={`${DATA_SOURCES_TEST_PREFIX}-append-table-mgr-modal-title`}
        >
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"table"} />
            {appstr("Manage Tables")}
          </span>
        </DialogTitle>
        <DialogContent customContentClass={"oc-modal-content-container"}>
          <div className={"oc-table-search-container"}>
            <Flex height={"100%"} direction={"column"} padding={"12px 16px"} gap={"8px"}>
              <Flex direction={"column"} gap={"16px"}>
                <Text modifier={"default"} size={"small"}>
                  {appstr("Manage tables info (long)")}
                </Text>
                <Flex justifyContent={"space-between"} alignItems={"center"} gap={"16px"}>
                  <div className={"oc-search-long"} style={{ width: "100%" }}>
                    <Search
                      clearable
                      inputProps={{
                        disabled: !appendTables.length,
                        spellCheck: false
                      }}
                      size={"medium"}
                      onInput={handleSearchInput}
                      placeholder={searchPlaceholder}
                      fillWidth
                      timeout={200}
                      onResetInput={handleSearchInput}
                      dataTestId={"fm-search-input"}
                    />
                  </div>
                  <div className={"oc-right-justify"}>
                    <Button
                      modifier={"secondary"}
                      onClick={createTable}
                      dataTestId={`${dataTestIdPrefix}-create`}
                    >
                      {appstr("Create table")}
                    </Button>
                  </div>
                </Flex>
              </Flex>

              <div className={cx("oc-list-container", "ocn-list-scrollbar")}>
                <List
                  value={selectedTableID}
                  showCheck={false}
                  listWidth="fill"
                  options={options}
                  optionSize={"large"}
                />
              </div>
            </Flex>
          </div>
        </DialogContent>
        {
          <DialogActions divider={true}>
            <DialogActions.Secondary onClick={onClose} dataTestId={`${dataTestIdPrefix}-close`}>
              {appstr("Close")}
            </DialogActions.Secondary>
          </DialogActions>
        }
      </Modal>
    </CentralizedModalContainer>
  );
};
